/**
 * Outil « Ondes et son » (Boîte à outils, physique-chimie 4e → Tle) — logique PURE.
 *
 * Quatre parties : propagation (une impulsion ou une onde sinusoïdale le long d'un axe, dans
 * l'air, l'eau, l'acier ou le vide ; retard en un point M, écho sur un obstacle, longueur
 * d'onde), son (enregistrement à l'oscilloscope : hauteur, timbre, niveau sonore en dB, domaine
 * audible ; en Tle, plusieurs sources et la distance), diffraction et interférences (fente, fentes
 * d'Young, figure sur l'écran) et effet Doppler (source qui avance, fronts d'onde). `lawLines`
 * écrit les phrases du prof selon le niveau ; `makeQuiz` fabrique les questions du défi.
 *
 * Valeurs des cartes : son ≈ 340 m/s dans l'air, ≈ 1 500 m/s dans l'eau, 5 000 à 6 000 m/s dans
 * l'acier (5 000 ici) ; I₀ = 10⁻¹² W·m⁻² ; domaine audible 20 Hz – 20 kHz.
 */

import {
  frNum,
  mcqOptions,
  pick,
  retryMcq,
  sigFixed,
  sigNum,
  type Mcq,
  type Rng,
} from '../../ui/mcq';

export const fr = frNum;
export const sig = sigNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'propagation' | 'sound' | 'diffraction' | 'doppler';

export const PARTS: ReadonlyArray<{ id: Part; label: string; minRank: number }> = [
  { id: 'propagation', label: 'Propagation', minRank: 3 },
  { id: 'sound', label: 'Son', minRank: 3 },
  { id: 'diffraction', label: 'Diffraction', minRank: 7 },
  { id: 'doppler', label: 'Doppler', minRank: 7 },
];

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => rank === null || rank >= p.minRank);
}

/* ---------- propagation ---------- */

export type Medium = 'air' | 'water' | 'steel' | 'vacuum';
export const MEDIA: Record<Medium, { label: string; v: number }> = {
  air: { label: 'Air', v: 340 },
  water: { label: 'Eau', v: 1500 },
  steel: { label: 'Acier', v: 5000 },
  vacuum: { label: 'Vide', v: 0 },
};

export type Signal = 'pulse' | 'sine';

export interface PropInput {
  medium: Medium;
  signal: Signal;
  /** fréquence de l'onde sinusoïdale (Hz) */
  f: number;
  /** position du point M (m) */
  d: number;
  /** obstacle (écho), impulsion seulement */
  wall: boolean;
  /** distance de l'obstacle (m) */
  D: number;
}

/** longueur de l'axe affiché (m) : 400 m pour une impulsion, 4 m pour une onde sinusoïdale */
export const axisLength = (signal: Signal) => (signal === 'pulse' ? 400 : 4);
/** durée de l'impulsion (s) : un clap bref */
export const PULSE_W = 0.02;
const PULSE_T0 = 2.5 * PULSE_W;

export const speedOf = (m: Medium) => MEDIA[m].v;

/** durée filmée (s) */
export function propDuration(input: PropInput): number {
  const v = speedOf(input.medium);
  if (v === 0) return input.signal === 'pulse' ? 1 : 4 / input.f;
  if (input.signal === 'pulse') {
    const far = input.wall ? 2 * input.D : Math.max(input.d, axisLength('pulse'));
    return PULSE_T0 + far / v + 4 * PULSE_W;
  }
  return axisLength('sine') / v + 3 / input.f;
}

const bump = (s: number) => Math.exp(-(((s - PULSE_T0) / (PULSE_W / 2.5)) ** 2));

/** déformation (u.a., entre −1 et 1) au point x à l'instant t ; source en x = 0 */
export function waveAt(input: PropInput, x: number, t: number): number {
  const v = speedOf(input.medium);
  if (v === 0) return x === 0 ? source(input, t) : 0;
  if (input.signal === 'pulse') {
    // l'obstacle arrête l'onde : au-delà, rien ; en deçà, l'onde incidente et l'écho
    if (input.wall && x > input.D) return 0;
    let y = bump(t - x / v);
    if (input.wall) y += bump(t - (2 * input.D - x) / v);
    return y;
  }
  const s = t - x / v;
  return s < 0 ? 0 : Math.sin(2 * Math.PI * input.f * s);
}

function source(input: PropInput, t: number): number {
  return input.signal === 'pulse' ? bump(t) : Math.sin(2 * Math.PI * input.f * t);
}

/** retard du point M sur la source : τ = d / v (∞ dans le vide) */
export const delayAt = (input: PropInput, x: number) =>
  speedOf(input.medium) === 0 ? Infinity : x / speedOf(input.medium);

/** longueur d'onde λ = v / f */
export const wavelength = (input: PropInput) => speedOf(input.medium) / input.f;

/* ---------- son ---------- */

export type Timbre = 'pure' | 'flute' | 'violin';
export const TIMBRES: Record<Timbre, { label: string; harmonics: readonly number[] }> = {
  pure: { label: 'Diapason', harmonics: [1] },
  flute: { label: 'Flûte', harmonics: [1, 0.45, 0.15, 0.05] },
  violin: { label: 'Violon', harmonics: [1, 0.5, 0.33, 0.25, 0.2, 0.17] },
};

/** signal du micro (u.a., max 1 en valeur absolue environ) */
export function soundAt(timbre: Timbre, f: number, t: number): number {
  const h = TIMBRES[timbre].harmonics;
  const norm = h.reduce((s, a) => s + a, 0);
  return h.reduce((s, a, k) => s + a * Math.sin(2 * Math.PI * (k + 1) * f * t), 0) / norm;
}

const SWEEPS = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100] as const;

/** balayage (ms par division, 10 divisions) : entre 2 et 5 périodes à l'écran */
export function sweepFor(f: number): number {
  const T = 1000 / f;
  return SWEEPS.find((s) => (10 * s) / T >= 2) ?? 100;
}

export type Domain = 'infrason' | 'audible' | 'ultrason';
export const domainOf = (f: number): Domain =>
  f < 20 ? 'infrason' : f > 20000 ? 'ultrason' : 'audible';

export const I0 = 1e-12;
/** puissance d'une source : 80 dB à 1 m */
export const P_SRC = 4 * Math.PI * 1e-4;
export const levelOf = (I: number) => 10 * Math.log10(I / I0);
export const intensityOfLevel = (L: number) => I0 * 10 ** (L / 10);
/** n sources identiques à la distance d (m) : I = n P / (4π d²) */
export const intensityAt = (n: number, d: number) => (n * P_SRC) / (4 * Math.PI * d * d);

export function riskOf(L: number): string {
  if (L >= 120) return 'douleur, danger immédiat';
  if (L >= 100) return 'dangereux en quelques minutes';
  if (L >= 85) return 'dangereux au bout de quelques heures';
  return 'sans risque';
}

/* ---------- diffraction et interférences ---------- */

export const LASERS = [
  { id: 'blue', label: 'Bleu', nm: 450, color: '#3b82f6' },
  { id: 'green', label: 'Vert', nm: 532, color: '#22c55e' },
  { id: 'red', label: 'Rouge', nm: 650, color: '#ef4444' },
] as const;
export type LaserId = (typeof LASERS)[number]['id'];
export const laserById = (id: LaserId) => LASERS.find((l) => l.id === id)!;

export interface DiffInput {
  laser: LaserId;
  /** largeur de la fente (µm) */
  a: number;
  /** distance fente – écran (m) */
  D: number;
  /** deux fentes (Young) */
  young: boolean;
  /** distance entre les fentes (µm) */
  b: number;
}

/** intensité relative (0 → 1) sur l'écran en X (m), petits angles */
export function screenIntensity(input: DiffInput, X: number): number {
  const lam = laserById(input.laser).nm * 1e-9;
  const u = (Math.PI * input.a * 1e-6 * X) / (lam * input.D);
  const env = u === 0 ? 1 : (Math.sin(u) / u) ** 2;
  if (!input.young) return env;
  return env * Math.cos((Math.PI * input.b * 1e-6 * X) / (lam * input.D)) ** 2;
}

/** demi-angle θ = λ / a (rad), largeur de la tache centrale L = 2λD/a (m), interfrange i = λD/b (m) */
export function diffMeasures(input: DiffInput) {
  const lam = laserById(input.laser).nm * 1e-9;
  const theta = lam / (input.a * 1e-6);
  return { lam, theta, L: 2 * input.D * theta, i: (lam * input.D) / (input.b * 1e-6) };
}

/* ---------- effet Doppler ---------- */

export const C_SOUND = 340;

/** fréquence reçue devant (la source s'approche) et derrière (elle s'éloigne) */
export function doppler(fE: number, vE: number, c = C_SOUND) {
  return { ahead: (fE * c) / (c - vE), behind: (fE * c) / (c + vE) };
}

/** fronts d'onde à l'instant t : émis toutes les `period` secondes depuis la position de la source */
export function wavefronts(vE: number, period: number, t: number, c = C_SOUND) {
  const out: Array<{ cx: number; r: number }> = [];
  for (let k = 0; k * period <= t; k++) {
    const te = k * period;
    out.push({ cx: vE * te, r: c * (t - te) });
  }
  return out;
}

/* ---------- écriture ---------- */

export function hz(f: number): string {
  if (f >= 1000) return `${sig(f / 1000)}${NB}kHz`;
  return `${sig(f)}${NB}Hz`;
}
export function secs(t: number): string {
  if (!Number.isFinite(t)) return '∞';
  if (t !== 0 && Math.abs(t) < 1) return `${sig(t * 1000)}${NB}ms`;
  return `${sig(t)}${NB}s`;
}
export function meters(d: number): string {
  if (d !== 0 && Math.abs(d) < 0.01) return `${sig(d * 1000)}${NB}mm`;
  if (d !== 0 && Math.abs(d) < 1) return `${sig(d * 100)}${NB}cm`;
  return `${sig(d)}${NB}m`;
}
export const dB = (L: number) => `${fr(L, 0)}${NB}dB`;
const VUNIT = (rank: number | null) => (rank !== null && rank >= 7 ? 'm·s⁻¹' : 'm/s');

/** écriture scientifique : « 1,0 × 10⁻⁴ » */
export const sci = (v: number, s = 2) => {
  const e = Math.floor(Math.log10(Math.abs(v)));
  const m = v / 10 ** e;
  const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
  const sup = String(Math.abs(e)).replace(/\d/g, (d) => SUP[Number(d)]!);
  return `${sigFixed(m, s)} × 10${e < 0 ? '⁻' : ''}${sup}`;
};

/* ---------- phrases du prof ---------- */

export function propLines(input: PropInput, t: number, rank: number | null): string[] {
  const out: string[] = [];
  const v = speedOf(input.medium);
  const V = VUNIT(rank);
  if (v === 0) {
    out.push(
      'Dans le vide, rien ne vibre autour de la source : le son ne se propage pas. Il lui faut un milieu matériel (solide, liquide ou gaz).',
    );
    out.push('La lumière, elle, se propage dans le vide (≈ 300 000 km/s).');
    return out;
  }
  const col = rank !== null && rank <= 4;
  out.push(
    col
      ? `Le son se propage de proche en proche dans ${input.medium === 'air' ? 'l’air' : input.medium === 'water' ? 'l’eau' : 'l’acier'}, à v ≈ ${fr(v, 0)} ${V}.`
      : `Onde mécanique progressive : la perturbation se propage de proche en proche, sans transport de matière, à la célérité v ≈ ${fr(v, 0)} ${V}.`,
  );
  if (input.signal === 'pulse' && input.wall) {
    const dt = (2 * input.D) / v;
    out.push(
      `Écho : l’aller-retour jusqu’à l’obstacle dure Δt = 2 × ${fr(input.D, 0)} ÷ ${fr(v, 0)} ≈ ${secs(dt)} ; d = v × Δt ÷ 2 = ${fr(input.D, 0)} m.`,
    );
  } else {
    const tau = input.d / v;
    out.push(
      col
        ? `Le son arrive en M (d = ${meters(input.d)}) au bout de t = d ÷ v = ${secs(tau)}.`
        : `Retard du point M : τ = d ÷ v = ${meters(input.d)} ÷ ${fr(v, 0)} ≈ ${secs(tau)} ; M reproduit le mouvement de la source avec ce retard.`,
    );
  }
  if (input.signal === 'sine') {
    const T = 1 / input.f;
    out.push(`Période T = 1 ÷ f = 1 ÷ ${fr(input.f, 0)} ≈ ${secs(T)}.`);
    if (rank === null || rank >= 6) {
      out.push(
        `Double périodicité : période T dans le temps, longueur d’onde λ = v × T = v ÷ f = ${fr(v, 0)} ÷ ${fr(input.f, 0)} ≈ ${meters(v / input.f)} dans l’espace.`,
      );
      out.push('Change de milieu : f reste celle de la source, v et λ changent.');
    }
  }
  if (t > 0 && input.signal === 'pulse' && !input.wall && t < input.d / v)
    out.push('Le front de l’onde n’a pas encore atteint M : M est immobile.');
  return out;
}

export interface SoundInput {
  f: number;
  timbre: Timbre;
  /** niveau sonore (dB), quand on ne raisonne pas en sources */
  L: number;
  /** Tle : nombre de sources identiques et distance (m) */
  n: number;
  dist: number;
}

/** niveau effectif : en Tle, calculé à partir des sources et de la distance */
export const soundLevel = (input: SoundInput, rank: number | null) =>
  rank === 7 ? levelOf(intensityAt(input.n, input.dist)) : input.L;

export function soundLines(input: SoundInput, rank: number | null): string[] {
  const out: string[] = [];
  const T = 1 / input.f;
  const dom = domainOf(input.f);
  out.push(
    dom === 'audible'
      ? `f = ${hz(input.f)} : son audible (20 Hz – 20 kHz), ${input.f < 250 ? 'plutôt grave' : input.f > 2000 ? 'aigu' : 'médium'}.`
      : dom === 'infrason'
        ? `f = ${hz(input.f)} < 20 Hz : infrason, l’oreille humaine ne l’entend pas.`
        : `f = ${hz(input.f)} > 20 kHz : ultrason, l’oreille humaine ne l’entend pas (le chien ou la chauve-souris, si).`,
  );
  out.push(`Période T = 1 ÷ f ≈ ${secs(T)} : c’est la durée d’un motif sur l’enregistrement.`);
  out.push(
    input.timbre === 'pure'
      ? 'Diapason : son pur, le motif est une sinusoïde.'
      : `${TIMBRES[input.timbre].label} : même hauteur (même période), mais un autre motif : c’est le timbre.`,
  );
  const L = soundLevel(input, rank);
  if (rank === 7) {
    const I = intensityAt(input.n, input.dist);
    out.push(`I = n × P ÷ (4π d²) ≈ ${sci(I, 2)} W·m⁻² ; L = 10 log(I ÷ I₀) ≈ ${dB(L)}.`);
    out.push(
      'Deux sources : I double, L augmente de 3 dB. Distance doublée : I divisée par 4, L baisse de 6 dB.',
    );
  } else {
    out.push(`Niveau sonore ${dB(L)} : ${riskOf(L)}.`);
    if (rank === null || rank >= 5)
      out.push('Les décibels ne s’additionnent pas : deux sons de 60 dB donnent ≈ 63 dB.');
  }
  return out;
}

export function diffLines(input: DiffInput): string[] {
  const m = diffMeasures(input);
  const lamNm = laserById(input.laser).nm;
  const out = [
    `θ = λ ÷ a = ${fr(lamNm, 0)} × 10⁻⁹ ÷ (${fr(input.a, 0)} × 10⁻⁶) ≈ ${sigFixed(m.theta, 2)} rad (demi-angle).`,
    `Tache centrale : L = 2 λ D ÷ a ≈ ${meters(m.L)}. Fente plus fine ou λ plus grande : tache plus large.`,
  ];
  if (input.young)
    out.push(
      `Fentes d’Young : interfrange i = λ D ÷ b = ${fr(lamNm, 0)} × 10⁻⁹ × ${fr(input.D, 1)} ÷ (${fr(input.b, 0)} × 10⁻⁶) ≈ ${meters(m.i)} ; franges brillantes : interférences constructives (δ = k λ).`,
    );
  return out;
}

export function dopplerLines(fE: number, vE: number): string[] {
  const d = doppler(fE, vE);
  if (vE === 0) return ['Source immobile : fronts d’onde concentriques, tout le monde reçoit f_E.'];
  return [
    `Devant (la source s’approche) : f_R = f_E × c ÷ (c − v_E) = ${fr(fE, 0)} × 340 ÷ (340 − ${fr(vE, 0)}) ≈ ${hz(d.ahead)} : plus aigu.`,
    `Derrière (elle s’éloigne) : f_R = f_E × c ÷ (c + v_E) ≈ ${hz(d.behind)} : plus grave.`,
    'À vitesse constante, f_R ne monte pas à l’approche : elle reste constante, puis chute au passage.',
  ];
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'vacuum'
  | 'media'
  | 'distance-time'
  | 'storm'
  | 'light-time'
  | 'period-freq'
  | 'audible'
  | 'echo'
  | 'oscillo'
  | 'pitch-timbre'
  | 'db-add'
  | 'delay'
  | 'lambda'
  | 'lambda-medium'
  | 'seism'
  | 'no-matter'
  | 'level'
  | 'attenuation'
  | 'diffraction'
  | 'young'
  | 'doppler';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  switch (rank) {
    case 3:
      return ['vacuum', 'media', 'distance-time', 'storm', 'light-time'];
    case 4:
      return ['period-freq', 'audible', 'echo', 'storm', 'oscillo'];
    case 5:
      return ['oscillo', 'pitch-timbre', 'db-add', 'period-freq', 'audible'];
    case 6:
      return ['no-matter', 'delay', 'lambda', 'lambda-medium', 'seism'];
    case 7:
      return ['level', 'attenuation', 'diffraction', 'young', 'doppler'];
    default:
      return ['media', 'period-freq', 'lambda', 'level', 'diffraction'];
  }
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({
  kind,
  question,
  options: mcqOptions(good, wrongs, rng),
  explain,
});
const u = (v: string, unit: string) => `${v}${NB}${unit}`;
const n2 = (v: number) => sigFixed(v, 2);

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'vacuum': {
      const [ctx, good] = pick(
        [
          [
            'Un réveil sonne sous une cloche dont on retire tout l’air. Que se passe-t-il ?',
            'On ne l’entend plus, mais on le voit vibrer',
          ],
          [
            'Deux astronautes, sans radio, sont à 10 m l’un de l’autre dans l’espace. L’un crie. L’autre…',
            'n’entend rien : il n’y a pas d’air pour transmettre le son',
          ],
        ] as const,
        rng,
      );
      const wrongs: W[] =
        good[0] === 'O'
          ? [
              [
                'On l’entend toujours aussi fort',
                'Sans air, plus rien ne transmet la vibration jusqu’à l’oreille.',
              ],
              [
                'On l’entend plus fort',
                'Le vide ne transmet pas le son : il ne peut pas le renforcer.',
              ],
              [
                'Il s’arrête de vibrer',
                'Le marteau vibre toujours ; c’est le son qui ne se propage plus.',
              ],
            ]
          : [
              [
                'l’entend, mais avec un peu de retard',
                'Le son ne se propage pas dans le vide, même lentement.',
              ],
              [
                'l’entend normalement',
                'Le son a besoin de matière (gaz, liquide, solide) pour se propager.',
              ],
              ['l’entend plus fort que sur Terre', 'Le vide ne transmet pas le son du tout.'],
            ];
      return q(
        kind,
        ctx,
        good,
        wrongs,
        'Le son est une vibration qui se transmet de proche en proche dans la matière : il ne se propage pas dans le vide (la lumière, si).',
        rng,
      );
    }
    case 'media': {
      const [m, good] = pick(
        [
          ['l’eau', '≈ 1 500 m/s'],
          ['l’air', '≈ 340 m/s'],
          ['l’acier', '5 000 à 6 000 m/s'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `Quelle est la vitesse du son dans ${m} ?`,
        good,
        [
          ['≈ 340 m/s', 'C’est la vitesse dans l’air.'],
          ['≈ 1 500 m/s', 'C’est la vitesse dans l’eau.'],
          ['5 000 à 6 000 m/s', 'C’est la vitesse dans l’acier.'],
          ['≈ 300 000 km/s', 'C’est la vitesse de la lumière, pas celle du son.'],
        ].filter(([t]) => t !== good) as W[],
        'Le son va plus vite dans les liquides et les solides que dans l’air : ≈ 340 m/s (air), ≈ 1 500 m/s (eau), 5 000 à 6 000 m/s (acier).',
        rng,
      );
    }
    case 'distance-time': {
      const d = pick([170, 340, 510, 680, 850], rng);
      const t = d / 340;
      return q(
        kind,
        `Tu es à ${d} m de la scène d’un concert, en plein air (son : 340 m/s). Au bout de combien de temps entends-tu une note ?`,
        u(fr(t, 2), 's'),
        [
          [u(fr(d * 340, 0), 's'), 't = d ÷ v : on divise la distance par la vitesse.'],
          [u(fr(340 / d, 2), 's'), 't = d ÷ v, pas v ÷ d.'],
          [u('0', 's'), 'Le son met du temps à se propager : il n’arrive pas instantanément.'],
        ],
        `t = d ÷ v = ${d} ÷ 340 = ${fr(t, 2)} s.`,
        rng,
      );
    }
    case 'storm': {
      const t = pick([3, 4, 5, 6, 9], rng);
      const d = 340 * t;
      return q(
        kind,
        `Le tonnerre arrive ${t} s après l’éclair (son : 340 m/s). À quelle distance la foudre est-elle tombée ?`,
        u(fr(d, 0), 'm'),
        [
          [
            u(fr(d / 2, 0), 'm'),
            'On ne divise pas par 2 : le son fait un aller simple, de la foudre jusqu’à toi.',
          ],
          [u(fr(340 / t, 0), 'm'), 'd = v × t : on multiplie la vitesse par la durée.'],
          [
            u(fr(t * 300000, 0), 'km'),
            'C’est le son (le tonnerre) qui arrive en retard, pas la lumière.',
          ],
        ],
        `La lumière de l’éclair arrive presque instantanément : d = 340 × ${t} = ${fr(d, 0)} m (environ 1 km toutes les 3 s).`,
        rng,
      );
    }
    case 'light-time': {
      const [obj, dkm] = pick(
        [
          ['la Lune', 384000],
          ['le Soleil', 1.5e8],
        ] as const,
        rng,
      );
      const t = dkm / 300000;
      const good = t < 10 ? u(fr(t, 1), 's') : u(fr(t / 60, 1), 'min');
      return q(
        kind,
        `${obj === 'la Lune' ? 'La Lune' : 'Le Soleil'} est à ${fr(dkm, 0)} km de la Terre. Combien de temps met sa lumière pour nous parvenir (c ≈ 300 000 km/s) ?`,
        good,
        [
          [
            t < 10 ? u(fr(t * 60, 0), 's') : u(fr(t, 0), 'min'),
            t < 10
              ? 'Le résultat est en secondes : ne le multiplie pas par 60.'
              : `${fr(t, 0)} s, ce n’est pas ${fr(t, 0)} min : divise par 60.`,
          ],
          [u('0', 's'), 'La lumière est très rapide, mais pas instantanée.'],
          [
            t < 10 ? u(fr(dkm / 340, 0), 's') : u(fr(dkm / 340 / 60, 0), 'min'),
            'C’est le calcul avec la vitesse du son ; la lumière va à 300 000 km/s.',
          ],
        ],
        `t = d ÷ c = ${fr(dkm, 0)} ÷ 300 000 ≈ ${t < 10 ? `${fr(t, 1)} s` : `${fr(t, 0)} s ≈ ${fr(t / 60, 1)} min`} : on voit ${obj} comme elle était un peu avant.`,
        rng,
      );
    }
    case 'period-freq': {
      const Tms = pick([1, 2, 2.5, 4, 5, 10], rng);
      const f = 1000 / Tms;
      return q(
        kind,
        `Un son a une période de ${fr(Tms, 1)} ms. Quelle est sa fréquence ?`,
        u(fr(f, 0), 'Hz'),
        [
          [
            u(fr(1 / Tms, 2), 'Hz'),
            `La période doit être en secondes : ${fr(Tms, 1)} ms = ${fr(Tms / 1000, 4)} s.`,
          ],
          [u(fr(Tms, 1), 'Hz'), 'f = 1 ÷ T : la fréquence est l’inverse de la période.'],
          [u(fr(Tms * 1000, 0), 'Hz'), 'On divise 1 par la période (en s), on ne multiplie pas.'],
        ],
        `f = 1 ÷ T = 1 ÷ ${fr(Tms / 1000, 4)} = ${fr(f, 0)} Hz.`,
        rng,
      );
    }
    case 'audible': {
      const [what, f, good] = pick(
        [
          [
            'Un sifflet pour chien émet à 25 kHz',
            25000,
            'Ultrason : l’oreille humaine ne l’entend pas',
          ],
          ['Un éléphant émet un son à 15 Hz', 15, 'Infrason : l’oreille humaine ne l’entend pas'],
          ['Une flûte joue une note à 880 Hz', 880, 'Son audible'],
          [
            'Une chauve-souris émet à 50 kHz',
            50000,
            'Ultrason : l’oreille humaine ne l’entend pas',
          ],
        ] as const,
        rng,
      );
      void f;
      return q(
        kind,
        `${what}. Ce son est…`,
        good,
        [
          ['Son audible', 'L’oreille humaine entend de 20 Hz à 20 kHz (20 000 Hz).'],
          [
            'Infrason : l’oreille humaine ne l’entend pas',
            'Infrason : fréquence inférieure à 20 Hz.',
          ],
          [
            'Ultrason : l’oreille humaine ne l’entend pas',
            'Ultrason : fréquence supérieure à 20 kHz.',
          ],
          [
            'Audible seulement s’il est assez fort',
            'Audible ou non dépend de la fréquence, pas de l’intensité.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'Domaine audible : de 20 Hz à 20 kHz. En dessous : infrasons ; au-dessus : ultrasons.',
        rng,
      );
    }
    case 'echo': {
      const [ctx, v, dt] = pick(
        [
          [
            'Un sonar émet dans l’eau (v = 1 500 m/s) ; l’écho revient au bout de',
            1500,
            pick([0.2, 0.4, 0.6, 0.8], rng),
          ],
          [
            'Tu cries face à une falaise (son : 340 m/s) ; l’écho revient au bout de',
            340,
            pick([1, 1.5, 2, 3], rng),
          ],
        ] as const,
        rng,
      );
      const d = (v * dt) / 2;
      return q(
        kind,
        `${ctx} ${fr(dt, 1)} s. À quelle distance est l’obstacle ?`,
        u(fr(d, 0), 'm'),
        [
          [u(fr(v * dt, 0), 'm'), 'Le signal fait l’aller ET le retour : on divise par 2.'],
          [u(fr(v / dt, 0), 'm'), 'd = v × Δt ÷ 2 : on multiplie la vitesse par la durée.'],
          [u(fr((v * dt) / 4, 0), 'm'), 'On divise par 2 une seule fois (aller + retour).'],
        ],
        `Aller-retour : ${fr(v, 0)} × ${fr(dt, 1)} = ${fr(v * dt, 0)} m ; aller : ${fr(v * dt, 0)} ÷ 2 = ${fr(d, 0)} m.`,
        rng,
      );
    }
    case 'oscillo': {
      const sweep = pick([0.5, 1, 2, 5], rng);
      const ndiv = pick([2, 4, 5], rng);
      const nmot = pick([2, 4], rng);
      const T = (ndiv * sweep) / nmot; // ms
      const span = ndiv * sweep;
      return q(
        kind,
        `À l’oscilloscope (balayage ${fr(sweep, 1)} ms par division), ${nmot} motifs complets occupent ${ndiv} divisions. Quelle est la période du son ?`,
        u(fr(T, 2), 'ms'),
        [
          [
            u(fr(span, 2), 'ms'),
            `${fr(span, 2)} ms, c’est la durée des ${nmot} motifs : divise par ${nmot}.`,
          ],
          [
            u(fr(sweep, 2), 'ms'),
            'Une division n’est pas une période : compte les divisions d’un motif.',
          ],
          [
            u(fr(span * nmot, 2), 'ms'),
            `On divise la durée par le nombre de motifs, on ne multiplie pas.`,
          ],
        ],
        `${nmot} motifs sur ${ndiv} × ${fr(sweep, 1)} = ${fr(span, 2)} ms : T = ${fr(span, 2)} ÷ ${nmot} = ${fr(T, 2)} ms (mesurer plusieurs motifs est plus précis).`,
        rng,
      );
    }
    case 'pitch-timbre': {
      const [ctx, good] = pick(
        [
          [
            'Un piano et une guitare jouent la même note. Sur les enregistrements, ils ont…',
            'La même période, des motifs différents',
          ],
          [
            'On joue la même note plus fort. Sur l’enregistrement, …',
            'L’amplitude augmente, la période ne change pas',
          ],
          ['On joue une note plus aiguë. Sur l’enregistrement, …', 'La période diminue'],
        ] as const,
        rng,
      );
      return q(
        kind,
        ctx,
        good,
        [
          [
            'La même période, des motifs différents',
            'Même note = même fréquence ; instrument différent = timbre différent.',
          ],
          [
            'L’amplitude augmente, la période ne change pas',
            'Plus fort = plus grande amplitude ; la hauteur ne change pas.',
          ],
          ['La période diminue', 'Plus aigu = fréquence plus grande = période plus petite.'],
          [
            'La période augmente',
            'Un son plus aigu a une fréquence plus grande, donc une période plus PETITE.',
          ],
        ].filter(([t]) => t !== good) as W[],
        'Hauteur ↔ fréquence (période) ; intensité ↔ amplitude ; timbre ↔ forme du motif.',
        rng,
      );
    }
    case 'db-add': {
      const L = pick([60, 70, 80, 90], rng);
      return q(
        kind,
        `Deux appareils identiques produisent chacun ${L} dB au même endroit. Les deux ensemble donnent environ…`,
        u(fr(L + 3, 0), 'dB'),
        [
          [
            u(fr(2 * L, 0), 'dB'),
            'Les décibels ne s’additionnent pas : ce sont les intensités qui s’ajoutent.',
          ],
          [
            u(fr(L, 0), 'dB'),
            'Deux sources font plus de bruit qu’une : le niveau augmente un peu.',
          ],
          [
            u(fr(L + 10, 0), 'dB'),
            '+ 10 dB correspond à une intensité 10 fois plus grande, pas 2 fois.',
          ],
        ],
        `Intensité doublée : le niveau augmente de 10 log 2 ≈ 3 dB, donc ≈ ${L + 3} dB.`,
        rng,
      );
    }
    case 'delay': {
      const v = pick([2, 2.5, 4, 5], rng);
      const d = pick([1, 1.5, 2, 3], rng);
      const tau = d / v;
      return q(
        kind,
        `Une onde se propage le long d’une corde à v = ${fr(v, 1)} m/s. Quel est le retard d’un point M situé à ${fr(d, 1)} m de la source ?`,
        u(n2(tau), 's'),
        [
          [u(n2(d * v), 's'), 'τ = d ÷ v : on divise la distance par la célérité.'],
          [u(n2(v / d), 's'), 'τ = d ÷ v, pas v ÷ d.'],
          [u('0', 's'), 'La perturbation met du temps à arriver : M bouge après la source.'],
        ],
        `τ = d ÷ v = ${fr(d, 1)} ÷ ${fr(v, 1)} = ${n2(tau)} s : M reproduit le mouvement de la source avec ce retard.`,
        rng,
      );
    }
    case 'lambda': {
      const f = pick([170, 340, 440, 680, 850], rng);
      const lam = 340 / f;
      return q(
        kind,
        `Un son de fréquence ${f} Hz se propage dans l’air (v = 340 m/s). Quelle est sa longueur d’onde ?`,
        u(n2(lam), 'm'),
        [
          [u(fr(340 * f, 0), 'm'), 'λ = v ÷ f : on divise la célérité par la fréquence.'],
          [u(n2(f / 340), 'm'), 'λ = v ÷ f, pas f ÷ v.'],
          [u(n2(1 / f), 'm'), '1 ÷ f est la période (en s), pas la longueur d’onde.'],
        ],
        `λ = v × T = v ÷ f = 340 ÷ ${f} ≈ ${n2(lam)} m.`,
        rng,
      );
    }
    case 'lambda-medium': {
      const f = pick([500, 1000, 2000], rng);
      return q(
        kind,
        `Un son de ${fr(f, 0)} Hz passe de l’air (340 m/s) dans l’eau (1 500 m/s). Que deviennent sa fréquence et sa longueur d’onde ?`,
        'f ne change pas ; λ augmente',
        [
          [
            'f augmente ; λ ne change pas',
            'La fréquence est fixée par la source : elle ne change pas d’un milieu à l’autre.',
          ],
          ['f et λ augmentent', 'f reste celle de la source ; seule λ = v ÷ f change.'],
          [
            'f ne change pas ; λ diminue',
            'v est plus grande dans l’eau, donc λ = v ÷ f est plus grande.',
          ],
        ],
        `f est imposée par la source ; λ = v ÷ f passe de ${n2(340 / f)} m à ${n2(1500 / f)} m.`,
        rng,
      );
    }
    case 'seism': {
      const vP = 6;
      const vS = 3.5;
      const dt = pick([10, 20, 30, 40], rng);
      const d = ((vP * vS) / (vP - vS)) * dt;
      return q(
        kind,
        `Une station enregistre les ondes S ${dt} s après les ondes P (v_P = 6,0 km/s, v_S = 3,5 km/s). Distance de l’épicentre ?`,
        u(fr(d, 0), 'km'),
        [
          [
            u(fr(vP * dt, 0), 'km'),
            'Δt est le décalage entre les deux arrivées, pas la durée du trajet : d ≠ v × Δt.',
          ],
          [u(fr(vS * dt, 0), 'km'), 'Δt est un décalage : d = v_P v_S Δt ÷ (v_P − v_S).'],
          [u(fr((vP - vS) * dt, 0), 'km'), 'Pars de Δt = d ÷ v_S − d ÷ v_P.'],
        ],
        `Δt = d ÷ v_S − d ÷ v_P donne d = v_P v_S Δt ÷ (v_P − v_S) = 6,0 × 3,5 × ${dt} ÷ 2,5 = ${fr(d, 0)} km.`,
        rng,
      );
    }
    case 'no-matter': {
      return q(
        kind,
        'Une vague passe sous un bouchon qui flotte, sans courant. Le bouchon…',
        'Monte puis redescend, sans avancer avec la vague',
        [
          [
            'Avance avec la vague jusqu’au rivage',
            'Une onde transporte de l’énergie, pas de la matière.',
          ],
          ['Reste immobile', 'La perturbation passe : le bouchon bouge, mais revient à sa place.'],
          ['Recule', 'L’onde ne fait pas reculer le bouchon : il oscille sur place.'],
        ],
        'Onde mécanique progressive : propagation d’une perturbation, sans transport de matière mais avec transport d’énergie.',
        rng,
      );
    }
    case 'level': {
      const I = pick([1e-6, 1e-5, 1e-4, 1e-3], rng);
      const L = levelOf(I);
      return q(
        kind,
        `Intensité sonore I = ${sci(I, 1)} W·m⁻² (I₀ = 1,0 × 10⁻¹² W·m⁻²). Quel est le niveau d’intensité sonore ?`,
        u(fr(L, 0), 'dB'),
        [
          [u(fr(L / 10, 0), 'dB'), 'L = 10 log(I ÷ I₀) : n’oublie pas le facteur 10.'],
          [u(fr(-L, 0), 'dB'), 'C’est I ÷ I₀, pas I₀ ÷ I : le niveau est positif ici.'],
          [
            u(fr(10 * Math.log10(I), 0), 'dB'),
            'Il faut diviser I par I₀ avant de prendre le logarithme.',
          ],
        ],
        `L = 10 log(I ÷ I₀) = 10 log(${sci(I, 1)} ÷ 10⁻¹²) = ${fr(L, 0)} dB.`,
        rng,
      );
    }
    case 'attenuation': {
      if (rng() < 0.5) {
        const L1 = pick([80, 90, 100], rng);
        return q(
          kind,
          `À 2 m d’une source ponctuelle, L = ${L1} dB. Que vaut L à 4 m (pas d’absorption) ?`,
          u(fr(L1 - 6, 0), 'dB'),
          [
            [
              u(fr(L1 / 2, 0), 'dB'),
              'Les niveaux ne se divisent pas : c’est l’intensité qui est divisée par 4.',
            ],
            [
              u(fr(L1 - 3, 0), 'dB'),
              'Distance doublée : I divisée par 4 (pas par 2), soit − 6 dB.',
            ],
            [
              u(fr(L1 - 12, 0), 'dB'),
              'I = P ÷ (4π d²) : distance doublée, I divisée par 4, soit 10 log 4 ≈ 6 dB.',
            ],
          ],
          `I = P ÷ (4π d²) : distance doublée, I divisée par 4, L baisse de 10 log 4 ≈ 6 dB : ${L1 - 6} dB.`,
          rng,
        );
      }
      const A = pick([10, 20, 30], rng);
      const k = 10 ** (A / 10);
      return q(
        kind,
        `Un casque antibruit apporte une atténuation de ${A} dB. Par combien divise-t-il l’intensité sonore ?`,
        fr(k, 0),
        [
          [fr(A, 0), `${A} dB ne divise pas l’intensité par ${A} : A = 10 log(I₁ ÷ I₂).`],
          [fr(A / 10, 0) === '1' ? '2' : fr(A / 10, 0), 'I₁ ÷ I₂ = 10^(A/10), pas A ÷ 10.'],
          [fr(2 * A, 0), 'I₁ ÷ I₂ = 10^(A/10).'],
        ],
        `A = 10 log(I₁ ÷ I₂) donne I₁ ÷ I₂ = 10^(${A}/10) = ${fr(k, 0)}.`,
        rng,
      );
    }
    case 'diffraction': {
      const lam = pick([450, 532, 650], rng);
      const a = pick([50, 100, 200], rng);
      const D = pick([1.5, 2, 2.5], rng);
      const L = (2 * lam * 1e-9 * D) / (a * 1e-6);
      const cm = (x: number) => u(n2(x * 100), 'cm');
      return q(
        kind,
        `Laser de λ = ${lam} nm, fente de largeur a = ${a} µm, écran à D = ${fr(D, 1)} m. Largeur de la tache centrale ?`,
        cm(L),
        [
          [cm(L / 2), 'θ est un DEMI-angle : la tache mesure 2 D θ = 2 λ D ÷ a.'],
          [cm(L * 1000), 'Attention aux conversions : nm = 10⁻⁹ m, µm = 10⁻⁶ m.'],
          [u(n2(L * 100), 'mm'), `L ≈ ${n2(L)} m, soit ${n2(L * 100)} cm : attention à l’unité.`],
        ],
        `L = 2 λ D ÷ a = 2 × ${lam} × 10⁻⁹ × ${fr(D, 1)} ÷ (${a} × 10⁻⁶) ≈ ${n2(L * 100)} cm.`,
        rng,
      );
    }
    case 'young': {
      if (rng() < 0.5) {
        const lam = pick([532, 650], rng);
        const b = pick([0.2, 0.25, 0.5], rng);
        const D = pick([1, 2], rng);
        const i = (lam * 1e-9 * D) / (b * 1e-3);
        const mm = (x: number) => u(n2(x * 1000), 'mm');
        return q(
          kind,
          `Fentes d’Young : λ = ${lam} nm, b = ${fr(b, 2)} mm, D = ${fr(D, 1)} m. Quel est l’interfrange ?`,
          mm(i),
          [
            [
              mm(i * 2),
              'i = λ D ÷ b, sans facteur 2 (c’est la tache de diffraction qui fait 2 λ D ÷ a).',
            ],
            [mm((b * 1e-3 * D) / (lam * 1e-9) / 1e6), 'i = λ D ÷ b : λ au numérateur.'],
            [
              mm(i / 1000),
              'Attention aux conversions : b en m (mm = 10⁻³ m), λ en m (nm = 10⁻⁹ m).',
            ],
          ],
          `i = λ D ÷ b = ${lam} × 10⁻⁹ × ${fr(D, 1)} ÷ (${fr(b, 2)} × 10⁻³) ≈ ${n2(i * 1000)} mm.`,
          rng,
        );
      }
      return q(
        kind,
        'Fentes d’Young : on rapproche les deux fentes (b plus petit). Les franges…',
        'S’écartent : l’interfrange augmente',
        [
          [
            'Se resserrent : l’interfrange diminue',
            'i = λ D ÷ b : b plus petit donne i plus grand.',
          ],
          ['Ne changent pas', 'L’interfrange dépend de b : i = λ D ÷ b.'],
          [
            'Disparaissent',
            'Les fentes restent éclairées par une source unique : il y a toujours des franges.',
          ],
        ],
        'i = λ D ÷ b : quand b diminue, l’interfrange augmente (les franges s’écartent).',
        rng,
      );
    }
    case 'doppler': {
      const fE = pick([400, 440, 500], rng);
      const vE = pick([17, 20, 34], rng);
      const fR = (fE * 340) / (340 - vE);
      return q(
        kind,
        `Une ambulance émet à f_E = ${fE} Hz et s’approche de toi à v_E = ${vE} m/s (c = 340 m/s). Quelle fréquence reçois-tu ?`,
        u(fr(fR, 0), 'Hz'),
        [
          [
            u(fr((fE * 340) / (340 + vE), 0), 'Hz'),
            'La source s’APPROCHE : le son reçu est plus aigu, f_R > f_E (c − v_E au dénominateur).',
          ],
          [
            u(fr(fE, 0), 'Hz'),
            'La source bouge : la fréquence reçue n’est pas celle émise (effet Doppler).',
          ],
          [
            u(fr(fE + vE, 0), 'Hz'),
            'f_R = f_E × c ÷ (c − v_E) : on n’ajoute pas une vitesse à une fréquence.',
          ],
        ],
        `f_R = f_E × c ÷ (c − v_E) = ${fE} × 340 ÷ (340 − ${vE}) ≈ ${fr(fR, 0)} Hz : plus aigu à l’approche.`,
        rng,
      );
    }
  }
}
